import { useMemo } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, AlertCircle, GraduationCap } from "lucide-react";

// EA attendance_status → badge styling
const STATUS_META = {
  present: { label: "Present", className: "bg-green-100 text-green-800 border-green-200" },
  excused: { label: "Excused", className: "bg-orange-100 text-orange-800 border-orange-200" },
  absent: { label: "Absent", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  refusal: { label: "Refusal", className: "bg-red-100 text-red-800 border-red-200" },
  pending: { label: "Pending", className: "bg-slate-100 text-slate-700 border-slate-200" },
};

const renderStatusBadge = (entry) => {
  // Top-level status='excused' is the "manually excused" flag used by RunningRoster
  // + Attendance bulk-excused + closeout — treat it as an Excused row here too.
  if ((entry.status || "").toLowerCase() === "excused") {
    return (
      <Badge className={STATUS_META.excused.className} variant="outline">
        {STATUS_META.excused.label}
      </Badge>
    );
  }
  const key = (entry.attendance_status || "pending").toLowerCase();
  const meta = STATUS_META[key] || STATUS_META.pending;
  return (
    <Badge className={meta.className} variant="outline">
      {meta.label}
    </Badge>
  );
};

export default function GradeLevelRoster() {
  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: rosterEntries = [], isLoading } = useQuery({
    queryKey: ["rosterEntries"],
    queryFn: () => base44.entities.RosterEntry.list(),
    enabled: !!user,
  });

  const grade = user?.teacher_grade_level;

  const gradeRows = useMemo(() => {
    if (!grade) return [];
    const g = String(grade).trim();
    // Active EA roster today = status IN ('active','excused') — same criterion
    // the closeout uses. Match by teacher_grade_level == entry.grade_level.
    const filtered = rosterEntries.filter((e) => {
      const entryStatus = (e.status || "").toLowerCase();
      if (entryStatus !== "active" && entryStatus !== "excused") return false;
      return String(e.grade_level || "").trim() === g;
    });
    // De-duplicate by student — one row per student even if they have multiple
    // subject entries today; keep the "most engaged" status (present > pending > absent order below).
    const priority = { present: 0, excused: 1, pending: 2, absent: 3, refusal: 4 };
    const bestByStudent = new Map();
    for (const e of filtered) {
      const key = (e.other_id || e.student_id || e.student_email || "").toString().trim();
      if (!key) continue;
      const existing = bestByStudent.get(key);
      const effectiveStatus =
        (e.status || "").toLowerCase() === "excused"
          ? "excused"
          : (e.attendance_status || "pending").toLowerCase();
      const rank = priority[effectiveStatus] ?? 99;
      if (!existing || rank < existing._rank) {
        bestByStudent.set(key, { ...e, _rank: rank });
      }
    }
    return Array.from(bestByStudent.values()).sort((a, b) => {
      const ln = (a.student_last_name || "").localeCompare(b.student_last_name || "");
      if (ln !== 0) return ln;
      return (a.student_first_name || "").localeCompare(b.student_first_name || "");
    });
  }, [rosterEntries, grade]);

  if (user && !grade) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive" data-testid="grade-not-set-alert">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Your grade level is not set. Ask an admin to set it on the Staff Manager page,
            then reload this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Grade Level Roster</h1>
            <p className="text-slate-600 mt-1">
              Students in Grade {grade} currently on today&apos;s EA roster.
            </p>
          </div>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Active EA Students
              <Badge variant="secondary" className="ml-2" data-testid="grade-roster-count">
                {gradeRows.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500">Loading roster…</div>
            ) : gradeRows.length === 0 ? (
              <div className="p-8 text-center text-slate-500" data-testid="grade-roster-empty">
                No students from Grade {grade} are on today&apos;s EA roster.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Assignment</TableHead>
                    <TableHead>Teacher</TableHead>
                    <TableHead>Cycle Average</TableHead>
                    <TableHead>Current EA Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gradeRows.map((e) => (
                    <TableRow
                      key={e.id}
                      data-testid={`grade-roster-row-${e.other_id || e.student_id}`}
                    >
                      <TableCell className="font-medium">
                        {e.student_first_name} {e.student_last_name}
                      </TableCell>
                      <TableCell>{e.assignment || "—"}</TableCell>
                      <TableCell>{e.teacher || e.teacher_name || "—"}</TableCell>
                      <TableCell className="font-semibold text-red-600">
                        {e.athletic_grade || "—"}
                      </TableCell>
                      <TableCell>{renderStatusBadge(e)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
